/**
 * @file BootTemplateInjector.ts
 * @description
 *
 * @typedef BootConfig the configuration data type that will be used to allow for expandable input to the class.
 *  type BootConfig = {
 *        deviceName: FTL_NULL_VALUE | string;
 *        deviceArch: Architecture;
 *        commAPI: uart_basic.ProcessedPayload | spi_basic.ProcessedPayload | undefined;
 *        delayHeader: HeaderFile | undefined | string;
 *        eeprom_size: number;
 *        flash_size: number;
 *        userInterfaceData: BootUserData; // Data that comes from the User Interface
 *        payload: BootTemplatePayload; // Data payload to return to the getTemplateData() function which follows 'pure' programming
 *  };
 *
 * @requires BootLogic.ts The worker file that hold ALL the logic to the module
 *
 */
import { deviceValidator } from "./deviceValidator";
import { getters } from "./BootLogic";
import {
    BootUserData,
    BootTemplatePayload,
    Architecture,
    VERIFICATION_SCHEME_VALUE,
    FTL_NULL_VALUE,
    UART_API,
    BOOT_COMMUNICATION_PROTOCOLS,
    BOOT_MEMORY_PARAMETERS,
} from "./BootTemplateInterfaces";
import { HeaderFile } from "@microchip/scf-interface";
import { decimal2Hex, parseFunctionAPI } from "./Helpers";

/**
 * This type will be used by the DerivedData file to pass the needed arguments
 *  from the state of the auto-module to the BootTemplateInjector
 */
type BootConfig = {
    deviceName: FTL_NULL_VALUE | string;
    deviceArch: Architecture;
    commAPI: UART_API | undefined; // Used for the interfaceId
    delayHeader: HeaderFile | undefined | string;
    nvmPayload: BOOT_MEMORY_PARAMETERS;
    userInterfaceData: BootUserData; // Data that comes from the User Interface
    payload: BootTemplatePayload; // Data payload to return to the getTemplateData() function which follows 'pure' programming
};

const createNullUserData = (): BootUserData => {
    return {
        BootRunningIO: undefined,
        BootEntryIO: undefined,
        IndicatorData: undefined,
        EntryData: undefined,
        FlashRead: undefined,
        EERead: undefined,
        EEWrite: undefined,
        ConfigWrite: undefined,
        ConfigRead: undefined,
        EraseMode: undefined,
        verification: undefined,
        ResetVectorAddress: 0,
        SelfProtect: undefined,
        Checksum: undefined,
    };
};
export const initBootPayload = (): BootTemplatePayload => {
    return {
        BL_UARTWrite: "unimplemented",
        BL_UARTRead: "unimplemented",
        BL_UARTIsRxReady: "unimplemented",
        BL_UARTIsTxReady: "unimplemented",
        BL_UARTTransmitDone: "unimplemented",
        BL_UARTAutoBaudSet: "unimplemented",
        BL_UARTAutoBaudQuery: "unimplemented",
        BL_USARTIsAutoBaudDetectError: "undefined_IsAutoBaudDetectError",
        BL_USARTAutoBaudDetectErrorReset: "undefined_AutoBaudDetectErrorReset",
        DELAY_HEADER_PATH: "NotApplicable",
        NVM_ACCESS_TYPE: "BYTE_ACCESS",
        RESET_BIT_U: "NotApplicable",
        SUPPORT_FLASH_READ: "Disabled",
        SUPPORT_EE_DATA_READ: "Disabled",
        SUPPORT_EE_DATA_WRITE: "Disabled",
        ERASE_CONTROL: "FullErase",
        AVR_PAGE: "NotApplicable",
        NEW_RESET_VECTOR_U: "0x00",
        NEW_INTERRUPT_VECTOR_LOW_U: "NotApplicable",
        NEW_INTERRUPT_VECTOR_HIGH_U: "NotApplicable",
        SELF_PROTECTION: "Disabled",
        CFGS_BIT: "NVMREGS",
        EECON1_SFR: "NVMCON1",
        EECON2_SFR: "NVMLOCK",
        EEDATL_SFR: "NVMDATL",
        EEDATH_SFR: "NVMDATH",
        EEADRL_SFR: "NVMADRL",
        EEADRH_SFR: "NVMADRH",
        DRAIN_RXBUF_AFTER_AB: "NotApplicable",
        SUPPORT_CONFIG_WRITE: "Disabled",
        SUPPORT_CONFIG_READ: "Disabled",
        SUPPORT_CHECKSUM: "Disabled",
        PIC18F_Q: "Disabled",
        DEVICE_FAMILY: "NotApplicable",
        DEVICE_TYPE: "NotApplicable",
        CHIP_TYPE: undefined,
        APP_VALID_VALUE_U: "0x55",
        NO_APPLICATION_CODE_U: "0xFF",
        CRC_SEED_U: "NotApplicable",
        CRC_SIZE_U: "NotApplicable",
        CRC_POLYNOMIAL_U: "NotApplicable",
        FUSE: "NotApplicable",
        CRC_XOR_U: "0x00000000",
        CHECKSUM_SIZE_U: "2",
        VERIFY: "RESET_VECTOR_VERIFICATION",
        BOOTLOADER_RUNNING_IO: "Disabled",
        IO_PIN_ENTRY: "Disabled",
        BL_INDICATOR_PIN_ACTIVATION: "Active_High",
        BL_ENTRY_POINT: "BL_ENTRY",
        BL_INDICATOR_PIN: "BL_INDICATOR",
        DEVICE_ID_START_ADDRESS: "0x3FFFFE",
        FLASH_SIZE_U: "0x00",
        EEPROM_START_U: "0x00",
        CONFIGURATIONS_BYTES_START_ADDRESS_U: "0x300000",
        USER_ID_START_ADDRESS_U: "0x200000",
        BOOT_BLOCK_SIZE_U: "512bytes",
        EEPROM_SIZE_U: "0x00",
    } as BootTemplatePayload;
};

/**
 * @class BootTemplateInjector
 * @classdesc Class implementation of all the logical functions and arguments that are used to feed the template files in SCF
 * @implements BootTemplateInterfaces.ts
 *   BootUserData: Used to pass UI information to the class
 *   BootTemplatePayload: used to house all of the tokens that are in the template files
 *   Architecture: device-meta architecure types
 */
export class BootTemplateInjector {
    private BootConfigData: BootConfig = {
        deviceArch: undefined,
        deviceName: "N/A",
        nvmPayload: {
            eeprom_size: 0,
            eeprom_start: 0,
            flash_size: 0,
        },
        commAPI: undefined,
        delayHeader: undefined,
        userInterfaceData: createNullUserData(),
        payload: initBootPayload(),
    };

    // Initialize the Injector Object
    constructor(
        commPayloads: BOOT_COMMUNICATION_PROTOCOLS | undefined,
        delayHeader: HeaderFile | undefined,
        deviceName: string,
        deviceArch: Architecture,
        nvmPayload: BOOT_MEMORY_PARAMETERS,
        userData: BootUserData,
    ) {
        // Build the delay header path if able
        if (delayHeader !== undefined) {
            const delayHeaderPath = delayHeader.path;
            const delayHeaderName = delayHeader.name;
            this.BootConfigData.delayHeader = `${delayHeaderPath}${delayHeaderName}`;
        } else {
            this.BootConfigData.delayHeader = "NotApplicable";
        }

        // Store device info
        this.BootConfigData.deviceArch = deviceArch;
        this.BootConfigData.deviceName = deviceName;

        // TODO: Evalute which communication protocol is non-empty,
        // there should only be one protocol used for the bootloader
        if (commPayloads !== undefined) {
            this.BootConfigData.commAPI = commPayloads.UART as UART_API;
        }

        this.BootConfigData.nvmPayload = nvmPayload;

        // values from user interface needs to be cleaned and converted to predicted types
        this.BootConfigData.userInterfaceData = {
            ...userData,
            verification: getters.getVerification(userData.verification),
            ResetVectorAddress: userData.ResetVectorAddress,
        } as BootUserData;
    }

    // Called from getTemplateData() to return the payload we have been manipulating
    inject = (): any => {
        return this.BootConfigData.payload;
    };

    setTemplateData = (): void => {
        this.BootConfigData.payload = {
            ...this.BootConfigData.payload,
            DELAY_HEADER_PATH: this.BootConfigData.delayHeader as string,
            APP_VALID_VALUE_U: "0x55",
            ...(getters.getRegisterNames(
                this.BootConfigData.deviceArch,
            ) as BootTemplatePayload),
            BL_UARTRead: parseFunctionAPI(
                this.BootConfigData.commAPI?.Read ?? "unimplemented",
                this.BootConfigData.commAPI?.moduleName,
            ),
            BL_UARTIsRxReady: parseFunctionAPI(
                this.BootConfigData.commAPI?.isRxReady ?? "unimplemented",
                this.BootConfigData.commAPI?.moduleName,
            ),
            BL_UARTWrite: parseFunctionAPI(
                this.BootConfigData.commAPI?.Write ?? "unimplemented",
                this.BootConfigData.commAPI?.moduleName,
            ),
            BL_UARTIsTxReady: parseFunctionAPI(
                this.BootConfigData.commAPI?.isTxReady ?? "unimplemented",
                this.BootConfigData.commAPI?.moduleName,
            ),
            BL_UARTTransmitDone: parseFunctionAPI(
                this.BootConfigData.commAPI?.TransmitDone ?? "unimplemented",
                this.BootConfigData.commAPI?.moduleName,
            ),
            BL_UARTAutoBaudSet: parseFunctionAPI(
                this.BootConfigData.commAPI?.AutoBaudSet ?? "unimplemented",
                this.BootConfigData.commAPI?.moduleName,
            ),
            BL_UARTAutoBaudQuery: parseFunctionAPI(
                this.BootConfigData.commAPI?.AutoBaudQuery ?? "unimplemented",
                this.BootConfigData.commAPI?.moduleName,
            ),
            BL_USARTIsAutoBaudDetectError:
                getters.getDeviceType(this.BootConfigData.deviceArch) == "PIC"
                    ? `${this.BootConfigData.commAPI?.moduleName}_IsAutoBaudDetectOverflow`
                    : `${this.BootConfigData.commAPI?.moduleName}_IsAutoBaudDetectError`,
            BL_USARTAutoBaudDetectErrorReset:
                getters.getDeviceType(this.BootConfigData.deviceArch) == "PIC"
                    ? `${this.BootConfigData.commAPI?.moduleName}_AutoBaudDetectOverflowReset`
                    : `${this.BootConfigData.commAPI?.moduleName}_AutoBaudDetectErrorReset`,
            NVM_ACCESS_TYPE: getters.getNVMAccessType(this.BootConfigData.deviceName),
            RESET_BIT_U: getters.getResetBits(
                this.BootConfigData.deviceArch,
                this.BootConfigData.deviceName,
            ),
            FUSE: getters.getFuse(
                this.BootConfigData.deviceArch,
                this.BootConfigData.deviceName,
            ),
            AVR_PAGE: getters.getAVRPage(
                this.BootConfigData.deviceArch,
                this.BootConfigData.deviceName,
            ),
            EEPROM_SIZE_U: decimal2Hex(this.BootConfigData.nvmPayload.eeprom_size),
            FLASH_SIZE_U: decimal2Hex(this.BootConfigData.nvmPayload.flash_size),
            EEPROM_START_U: decimal2Hex(this.BootConfigData.nvmPayload.eeprom_start),
            USER_ID_START_ADDRESS_U: "0x200000", // Address is identical for all PIC18F devices we support
            BOOT_BLOCK_SIZE_U: getters.getBootBlockSize(this.BootConfigData.deviceName),
            DEVICE_TYPE: getters.getDeviceType(this.BootConfigData.deviceArch),
            CHIP_TYPE: getters.getChipType(
                this.BootConfigData.deviceArch,
                this.BootConfigData.deviceName,
            ),
            DRAIN_RXBUF_AFTER_AB: getters.getDrainRXBUF(
                this.BootConfigData.deviceArch,
                this.BootConfigData.deviceName,
            ),
            DEVICE_FAMILY: getters.isPICKXXDeviceFamily(this.BootConfigData.deviceName),
            PIC18F_Q: getters.convertBool2MCC(
                deviceValidator.isPIC18Q(this.BootConfigData.deviceName),
            ),
            SUPPORT_CONFIG_WRITE: getters.convertBool2MCC(
                this.BootConfigData.userInterfaceData.ConfigWrite,
            ),
            SUPPORT_CONFIG_READ: getters.convertBool2MCC(
                this.BootConfigData.userInterfaceData.ConfigRead,
            ),
            NO_APPLICATION_CODE_U: getters.getNoAppCode(this.BootConfigData.deviceArch),
            DEVICE_ID_START_ADDRESS: getters.getDeviceIdStart(
                this.BootConfigData.deviceArch,
            ),
            // TODO: this is a problem. Secure boot needs a better way of finding these values
            CONFIGURATIONS_BYTES_START_ADDRESS_U: "0x300000",
            BOOTLOADER_RUNNING_IO: getters.convertBool2MCC(
                this.BootConfigData.userInterfaceData.BootRunningIO,
            ),
            IO_PIN_ENTRY: getters.convertBool2MCC(
                this.BootConfigData.userInterfaceData.BootEntryIO,
            ),
            SUPPORT_FLASH_READ: getters.convertBool2MCC(
                this.BootConfigData.userInterfaceData.FlashRead,
            ),
            SUPPORT_EE_DATA_READ: getters.convertBool2MCC(
                this.BootConfigData.userInterfaceData.EERead,
            ),
            SUPPORT_EE_DATA_WRITE: getters.convertBool2MCC(
                this.BootConfigData.userInterfaceData.EEWrite,
            ),
            SUPPORT_CHECKSUM: getters.convertBool2MCC(
                this.BootConfigData.userInterfaceData.Checksum,
            ),
            ERASE_CONTROL: this.BootConfigData.userInterfaceData.EraseMode,
            VERIFY: this.BootConfigData.userInterfaceData
                .verification as VERIFICATION_SCHEME_VALUE,
            ...(getters.getCRCData(
                this.BootConfigData.userInterfaceData
                    .verification as VERIFICATION_SCHEME_VALUE,
            ) as BootTemplatePayload),
            NEW_RESET_VECTOR_U: decimal2Hex(
                this.BootConfigData.userInterfaceData.ResetVectorAddress as number,
            ),
            NEW_INTERRUPT_VECTOR_LOW_U: getters.getInterruptVectorRange(
                this.BootConfigData.userInterfaceData.ResetVectorAddress as number,
                "low",
                this.BootConfigData.deviceArch,
            ),
            NEW_INTERRUPT_VECTOR_HIGH_U: getters.getInterruptVectorRange(
                this.BootConfigData.userInterfaceData.ResetVectorAddress as number,
                "high",
                this.BootConfigData.deviceArch,
            ),
            SELF_PROTECTION: getters.convertBool2MCC(
                this.BootConfigData.userInterfaceData.SelfProtect,
            ),
            BL_ENTRY_POINT: getters.getCustomCname(
                "ioInPin",
                this.BootConfigData.userInterfaceData.EntryData,
            ),
            BL_INDICATOR_PIN: getters.getCustomCname(
                "ioOutPin",
                this.BootConfigData.userInterfaceData.IndicatorData,
            ),
            BL_INDICATOR_PIN_ACTIVATION: getters.getIndicatorActivation(
                this.BootConfigData.userInterfaceData.IndicatorData,
            ),
        };
    };
}
